import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useArticleContext } from '../context/ArticleContext';
import MetadataPanel from './MetadataPanel';
import WordCounter from './WordCounter';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Jodit } from 'jodit';

const { FiSave, FiEye, FiCode, FiUpload, FiArrowLeft, FiLoader, FiUsers, FiUser } = FiIcons;

const Editor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { saveArticle, getArticle, importFromWebhook, articles } = useArticleContext();
  const editorRef = useRef(null);
  const joditEditor = useRef(null);
  const [article, setArticle] = useState({
    id: null,
    title: '',
    content: '',
    title_tag: '',
    meta_description: '',
    owner: '',
    associated_user: '',
    created_at: new Date(),
    updated_at: new Date()
  });
  const [viewMode, setViewMode] = useState('wysiwyg'); // 'wysiwyg' or 'html'
  const [htmlContent, setHtmlContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [lastContentUpdate, setLastContentUpdate] = useState(Date.now());

  // Get unique owners and users from existing articles for the dropdown
  const existingOwners = [...new Set(articles.map(a => a.owner).filter(Boolean))].sort();
  const existingUsers = [...new Set(articles.map(a => a.associated_user).filter(Boolean))].sort();

  // Load article data when ID is present
  useEffect(() => {
    if (id) {
      const existingArticle = getArticle(id);
      if (existingArticle) {
        setArticle({
          id: existingArticle.id,
          title: existingArticle.title || '',
          content: existingArticle.content || '',
          title_tag: existingArticle.title_tag || '',
          meta_description: existingArticle.meta_description || '',
          owner: existingArticle.owner || '',
          associated_user: existingArticle.associated_user || '',
          created_at: existingArticle.created_at,
          updated_at: existingArticle.updated_at
        });
        setHtmlContent(existingArticle.content || '');
      }
    } else {
      // Reset for new article
      setArticle({
        id: null,
        title: '',
        content: '',
        title_tag: '',
        meta_description: '',
        owner: '',
        associated_user: '',
        created_at: new Date(),
        updated_at: new Date()
      });
      setHtmlContent('');
    }
  }, [id, getArticle]);

  // Initialize Jodit editor when viewMode is wysiwyg
  useEffect(() => {
    if (viewMode === 'wysiwyg' && editorRef.current && !joditEditor.current) {
      joditEditor.current = Jodit.make("#editor", {
        direction: "ltr",
        showXPathInStatusbar: false,
        buttons: "bold,italic,underline,ul,ol,font,fontsize,paragraph,lineHeight,superscript,subscript,classSpan,file,image,copy,paste,selectall,copyformat,hr",
        height: 600,
        uploader: { insertImageAsBase64URI: true },
        toolbarAdaptive: false
      });

      // Handle changes
      joditEditor.current.events.on("change", (newContent) => {
        console.log("📝 Updated content:", newContent);
        setArticle(prev => ({ ...prev, content: newContent }));
        setLastContentUpdate(Date.now());
        // Optional: save content to a webhook or Supabase when edited
        // fetch("https://prosomoinc.app.n8n.cloud/webhook-test/72837c46-b27d-478d-8f83-8f6867b48d63", {
        //   method: "POST",
        //   headers: { "Content-Type": "application/json" },
        //   body: JSON.stringify({ content: newContent }),
        // });
      });
    } else if (viewMode === 'html' && joditEditor.current) {
      // Destroy Jodit when switching to HTML view
      joditEditor.current.destroy();
      joditEditor.current = null;
    }
    return () => {
      if (joditEditor.current) {
        joditEditor.current.destroy();
        joditEditor.current = null;
      }
    };
  }, [viewMode]);

  // Update Jodit editor content when article.content changes (for fetched data)
  useEffect(() => {
    if (joditEditor.current && viewMode === 'wysiwyg' && article.content !== undefined) {
      joditEditor.current.value = article.content;
    }
  }, [article.content, viewMode]);

  // Ensure content always starts with a paragraph and force paragraph tags
  const normalizeContent = (content) => {
    if (!content || content.trim() === '') {
      return '<p>&nbsp;</p>';
    }
    // If content doesn't start with a paragraph, wrap it
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const firstChild = tempDiv.firstChild;
    if (!firstChild || (firstChild.tagName && firstChild.tagName.toLowerCase() !== 'p')) {
      // Wrap in paragraph
      tempDiv.innerHTML = '<p>' + content + '</p>';
    }
    // Force all text nodes to be inside paragraph tags
    const forceParagraphs = (node) => {
      const childNodes = Array.from(node.childNodes);
      childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) {
          // Wrap loose text in paragraphs
          const p = document.createElement('p');
          p.innerHTML = child.textContent.replace(/\n/g, '</p><p>');
          node.replaceChild(p, child);
        } else if (child.nodeType === Node.ELEMENT_NODE && !['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'div'].includes(child.tagName.toLowerCase())) {
          forceParagraphs(child);
        }
      });
    };
    forceParagraphs(tempDiv);
    return tempDiv.innerHTML;
  };

  const handleHtmlChange = (e) => {
    const content = e.target.value;
    setHtmlContent(content);
    setArticle(prev => ({ ...prev, content }));
  };

  const toggleViewMode = () => {
    if (viewMode === 'wysiwyg') {
      // Switch to HTML view
      if (joditEditor.current) {
        const currentContent = joditEditor.current.value;
        setHtmlContent(currentContent);
        setArticle(prev => ({ ...prev, content: currentContent }));
        joditEditor.current.destroy();
        joditEditor.current = null;
      }
      setViewMode('html');
    } else {
      // Switch to WYSIWYG view
      setArticle(prev => ({ ...prev, content: htmlContent }));
      setViewMode('wysiwyg');
    }
  };

  const validateHTML = (html) => {
    // Basic HTML validation - ensure semantic structure is preserved
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    // Check for preserved placeholders
    const placeholders = html.match(/\[.*?\]/g) || [];
    return { isValid: true, // Basic validation passed
      placeholderCount: placeholders.length, warnings: [] };
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('');
    try {
      // Ensure we have the absolute latest content from the active editor view
      let currentContent = article.content;
      if (viewMode === 'wysiwyg' && joditEditor.current) {
        currentContent = joditEditor.current.value;
      } else if (viewMode === 'html') {
        currentContent = htmlContent;
      }
      // Update article state with latest content
      setArticle(prev => ({ ...prev, content: currentContent }));

      // Normalize content to ensure paragraphs
      const normalizedContent = normalizeContent(currentContent);

      // Validate HTML before saving
      const validation = validateHTML(normalizedContent);
      if (!validation.isValid) {
        setSaveStatus('HTML validation failed');
        return;
      }

      // Ensure owner is set (required field)
      if (!article.owner.trim()) {
        setSaveStatus('Please select or enter a client/owner');
        return;
      }

      // Associated user is optional, so no validation needed

      // Prepare article data for saving, ensuring all current values including owner and associated_user changes
      const articleToSave = {
        ...article,
        content: normalizedContent,
        title: article.title,
        title_tag: article.title_tag,
        meta_description: article.meta_description,
        owner: article.owner.trim(),
        associated_user: article.associated_user || null
      };

      const savedArticle = await saveArticle(articleToSave);
      setArticle(prev => ({
        ...prev,
        id: savedArticle.id,
        updated_at: savedArticle.updated_at
      }));
      setSaveStatus('Article saved successfully!');
      // Navigate back to list after successful save
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error) {
      console.error('Error saving article:', error);
      setSaveStatus(`Error: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleWebhookImport = async (webhookData) => {
    try {
      const parsedData = JSON.parse(webhookData);
      // Map webhook data to article structure
      setArticle(prev => ({
        ...prev,
        title: parsedData.title || 'Imported Article',
        content: parsedData.content || parsedData.html || '',
        title_tag: parsedData.title_tag || parsedData.metaTitle || parsedData.meta_title || '',
        meta_description: parsedData.meta_description || parsedData.metaDescription || '',
        owner: parsedData.owner || parsedData.client || prev.owner,
        associated_user: parsedData.associated_user || parsedData.user || parsedData.author || prev.associated_user
      }));
      setShowWebhookModal(false);
      setSaveStatus('Article imported from webhook');
    } catch (error) {
      alert('Invalid JSON format. Please check your webhook data.');
    }
  };

  const wordCount = article.content ? article.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length : 0;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <a
  href="https://article.prosomo.com/"
  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
>
  <SafeIcon icon={FiArrowLeft} className="text-lg" />
  <span>Back to Articles</span>
</a>
            <div className="flex items-center space-x-3">
              <button onClick={() => setShowWebhookModal(true)} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                <SafeIcon icon={FiUpload} className="text-sm" />
                <span>Import Webhook</span>
              </button>
              <button onClick={toggleViewMode} className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                <SafeIcon icon={viewMode === 'wysiwyg' ? FiCode : FiEye} className="text-sm" />
                <span>{viewMode === 'wysiwyg' ? 'HTML View' : 'WYSIWYG'}</span>
              </button>
              <button onClick={handleSave} disabled={isSaving} className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                <SafeIcon icon={isSaving ? FiLoader : FiSave} className={`text-sm ${isSaving ? 'animate-spin' : ''}`} />
                <span>{isSaving ? 'Saving...' : 'Save to Supabase'}</span>
              </button>
            </div>
          </div>
          {saveStatus && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${saveStatus.includes('Error') ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-green-100 text-green-700 border border-green-200'}`}>
              {saveStatus}
            </div>
          )}
          {/* Client/Owner and User Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Client/Owner Selection */}
            <div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiUsers} className="text-lg text-purple-500" />
                <label className="text-sm font-medium text-gray-700">Client/Owner:</label>
              </div>
              <div className="mt-2">
                <input type="text" value={article.owner} onChange={(e) => setArticle(prev => ({ ...prev, owner: e.target.value }))} placeholder="Enter client or owner name" list="existing-owners" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                <datalist id="existing-owners">
                  {existingOwners.map(owner => (
                    <option key={owner} value={owner} />
                  ))}
                </datalist>
              </div>
              <p className="text-xs text-gray-500 mt-1"> Required. Articles will be grouped by this field. </p>
            </div>
            {/* Associated User Selection */}
            <div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiUser} className="text-lg text-green-500" />
                <label className="text-sm font-medium text-gray-700">Associated User:</label>
              </div>
              <div className="mt-2">
                <input type="text" value={article.associated_user} onChange={(e) => setArticle(prev => ({ ...prev, associated_user: e.target.value }))} placeholder="Enter user name or ID (optional)" list="existing-users" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                <datalist id="existing-users">
                  {existingUsers.map(user => (
                    <option key={user} value={user} />
                  ))}
                </datalist>
              </div>
              <p className="text-xs text-gray-500 mt-1"> Optional. Can be used to organize articles by user. </p>
            </div>
          </div>
          <input type="text" value={article.title} onChange={(e) => setArticle(prev => ({ ...prev, title: e.target.value }))} placeholder="Article Title" className="w-full text-2xl font-bold border-none outline-none focus:ring-0 p-0 bg-transparent placeholder-gray-400" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 p-6">
          {/* Main Editor */}
          <div className="lg:col-span-3 space-y-4">
            <div className="border border-gray-200 rounded-lg">
              {viewMode === 'wysiwyg' ? (
                <div id="editor" ref={editorRef}></div>
              ) : (
                <textarea value={htmlContent} onChange={handleHtmlChange} onBlur={handleHtmlChange} className="w-full h-96 p-6 font-mono text-sm border-none outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset resize-none" style={{ minHeight: '500px' }} placeholder="Enter semantic HTML content here..." />
              )}
            </div>
            <WordCounter count={wordCount} target={1500} />
          </div>
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <MetadataPanel metaTitle={article.title_tag} metaDescription={article.meta_description} onMetaTitleChange={(value) => setArticle(prev => ({ ...prev, title_tag: value }))} onMetaDescriptionChange={(value) => setArticle(prev => ({ ...prev, meta_description: value }))} owner={article.owner} associatedUser={article.associated_user} />
          </div>
        </div>
      </div>
      {/* Webhook Import Modal */}
      {showWebhookModal && (
        <WebhookModal onClose={() => setShowWebhookModal(false)} onImport={handleWebhookImport} />
      )}
    </div>
  );
};

const WebhookModal = ({ onClose, onImport }) => {
  const [webhookData, setWebhookData] = useState('');
  const handleSubmit = (e) => {
    e.preventDefault();
    onImport(webhookData);
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
        <h2 className="text-xl font-bold mb-4">Import Article from Webhook</h2>
        <form onSubmit={handleSubmit}>
          <textarea value={webhookData} onChange={(e) => setWebhookData(e.target.value)} placeholder='Paste webhook JSON data here, e.g.: {"title": "Article Title", "content": "<h1>Heading</h1><p>Content with [Image suggestion] placeholders</p>", "title_tag": "SEO Title Tag", "meta_description": "SEO Meta Description", "owner": "Client Name", "associated_user": "User Name"}' className="w-full h-64 p-4 border border-gray-300 rounded-lg font-mono text-sm" />
          <div className="flex justify-end space-x-3 mt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Import to Supabase
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Editor;